// SPDX-License-Identifier: GPL-3.0-or-later
// Prod, the server SigmaBot: the wizard target (THEME.md section 5.8).
// A robot bot. In ASK MODE: PROD BOT it shows 8 health LEDs on its brow. Each correct DENY shot makes one LED safe.

import { type Mesh, type MeshStandardMaterial, SphereGeometry } from "three";
import { clamp, numberArg } from "../animation.ts";
import { BRAND_HEX } from "../art/canvas.ts";
import { drawTextPanel } from "../art/labels.ts";
import { addBot, botFront } from "./bot.ts";
import {
  type Built,
  type CharacterEnv,
  disposeTree,
  makeLabel,
  motion,
  namedGroup,
  put,
  sec,
  solid,
} from "./common.ts";
import type { SigmaBotSpec } from "./sigmabot/index.ts";

/** The look of Prod. */
export const PROD_SPEC: SigmaBotSpec = {
  body: "robot",
  palette: "slate",
  eyes: "wide",
  mouth: "flat",
  hat: "none",
  accessory: "antenna",
};

/** Size of one body unit of Prod. */
const SCALE = 1.2;

export const PROD_LED_COUNT = 8;

/** LED state: off (not in the wizard mode), safe, or pending. */
export type LedState = "off" | "safe" | "pending";

/** The state of each LED for a `leds:` arg. `off` switches all LEDs off. */
export function ledStates(arg: string): LedState[] {
  if (arg === "off") return Array.from({ length: PROD_LED_COUNT }, () => "off");
  const safe = Math.round(clamp(numberArg(arg, 0), 0, PROD_LED_COUNT));
  return Array.from({ length: PROD_LED_COUNT }, (_, i) => (i < safe ? "safe" : "pending"));
}

const LED_COLOR: Readonly<Record<LedState, number>> = {
  off: BRAND_HEX.gray333,
  safe: BRAND_HEX.edge,
  pending: BRAND_HEX.mark,
};

export function buildProd(env: CharacterEnv): Built {
  const group = namedGroup("Prod");

  addBot(group, "ProdBot", PROD_SPEC, SCALE);

  const leds: Mesh<SphereGeometry, MeshStandardMaterial>[] = [];
  for (let i = 0; i < PROD_LED_COUNT; i++) {
    const led = put(
      new SphereGeometry(3, 8, 8),
      solid(BRAND_HEX.gray333, { emissive: 0x000000 }),
      -28 + i * 8,
      67,
      botFront(PROD_SPEC, SCALE, 67, -28 + i * 8) + 0.8,
    );
    led.name = `ProdLed${i}`;
    leds.push(led);
    group.add(led);
  }
  const label = makeLabel(env, 50, 12, 256, 64, (ctx, w, h) => drawTextPanel(ctx, w, h, ["PROD"]));
  label.mesh.position.set(0, 14, botFront(PROD_SPEC, SCALE, 14) + 0.4);
  group.add(label.mesh);

  let states = ledStates("off");
  return {
    group,
    tick(timeMs) {
      const a = motion(env);
      const firstPending = states.indexOf("pending");
      leds.forEach((led, i) => {
        const state = states[i] ?? "off";
        led.material.color.setHex(LED_COLOR[state]);
        // A pending LED glows. The first pending LED pulses. With reduced motion there is no pulse.
        if (state === "pending") {
          led.material.emissive.setHex(BRAND_HEX.mark);
          led.material.emissiveIntensity =
            i === firstPending && a > 0 ? 0.5 + 0.5 * Math.sin(sec(timeMs) * 2) : 1;
        } else {
          led.material.emissive.setHex(0x000000);
          led.material.emissiveIntensity = 0;
        }
      });
    },
    play(name, arg) {
      if (name === "leds") states = ledStates(arg);
    },
    dispose() {
      disposeTree(group);
    },
  };
}
